import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Download } from 'lucide-react';
import { Button } from '../../components/ui/button';

// PRINTABLE QR for an invite link (Run P §2). Uses `qrcode` (the small, well-known npm
// package): SVG for print and PNG for slides, both generated in the browser, nothing sent
// anywhere. ⚠️ The QR encodes the invite URL and nothing else, so it grants exactly what
// the link grants (and no more: it dies with the link's expiry or use count).

function download(filename: string, href: string): void {
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function InviteQr({ url, label }: { url: string; label: string }) {
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    QRCode.toString(url, { type: 'svg', margin: 2, errorCorrectionLevel: 'M', width: 320 })
      .then((s) => {
        if (!cancelled) setSvg(s);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  const safeName = label.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'invite';

  const downloadSvg = () => {
    if (!svg) return;
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const href = URL.createObjectURL(blob);
    download(`${safeName}-qr.svg`, href);
    URL.revokeObjectURL(href);
  };
  const downloadPng = async () => {
    const href = await QRCode.toDataURL(url, { margin: 2, errorCorrectionLevel: 'M', width: 1200 });
    download(`${safeName}-qr.png`, href);
  };

  if (error) return <p className="text-xs text-rose-600">Could not make the QR code: {error}</p>;
  return (
    <div className="flex flex-wrap items-center gap-4 rounded-md border bg-white p-3">
      <div
        className="h-40 w-40 shrink-0"
        aria-label={`QR code for ${label}`}
        // The SVG is generated locally by the qrcode library from our own URL: not user markup.
        dangerouslySetInnerHTML={{ __html: svg ?? '' }}
      />
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground">
          Scan to open the invite page. The code dies with the link (expiry or last use).
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={downloadSvg} disabled={!svg}>
            <Download className="h-4 w-4" />
            SVG (print)
          </Button>
          <Button variant="outline" size="sm" onClick={() => void downloadPng()}>
            <Download className="h-4 w-4" />
            PNG
          </Button>
        </div>
      </div>
    </div>
  );
}
